import {
  IList,
  promiseState,
  useDataStream,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo } from 'react';
import { map, switchMap } from 'rxjs/operators';
import { viewTexts } from '../../../constants/view-texts';
import { rankToStar } from '../../../functions/convert-to-view-value/rank-to-star';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { mapListLatest } from '../../../global-state/database/common/map-latest';
import { trainingListOf } from '../../../global-state/database/model-training-list';
import { modelId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { IImageInfoType } from '../../../types/record/image-info';
import { ILabelFromApiType } from '../../../types/record/label';
import { IModelType } from '../../../types/record/model';
import { ITrainingInfoType } from '../../../types/record/training-info';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { ModelDetailView } from './model-detail-view';
import { useConditionalViewValues } from './use-conditional-view-values';
import { useModelVersionHandler } from './use-model-version-handler';
import { useStartTrainingButtonHandler } from './use-start-training-button-handler';

interface Props {
  model: IModelType;
  labelList: IList<ILabelFromApiType>;
  imageList: IList<IImageInfoType>;
  onEditLabelClick: () => void;
  onEditModelImagesClick: () => void;
}

export const ModelDetail: FC<Props> = memo(
  ({
    model,
    labelList,
    imageList,
    onEditLabelClick,
    onEditModelImagesClick
  }: Readonly<Props>) => {
    const modelsHistory$ = useDataStream<
      ListWithPromiseState<ITrainingInfoType>
    >(
      listWithPromiseStateInit(),
      // TODO: 一度一覧に戻って再度同じmodelIdの推論モデルを開いたときにリフレッシュされないのを直す
      modelId$.pipe(
        switchMap(trainingListOf),
        map(list =>
          mapListLatest(list, l => l.filter(e => e.status === 'completed'))
        )
      )
    );

    const modelsHistoryViewValue$ = useDataStream<
      ListWithPromiseState<{ id: number; label: string }>
    >(
      listWithPromiseStateInit(),
      modelsHistory$.pipe(
        map(list =>
          mapListLatest(list, l =>
            l.map(e => ({
              id: e.trainingId,
              label: `${new Date(
                e.dateFinished
              ).toLocaleString()} （${rankToStar(e.rank)}）`
            }))
          )
        )
      )
    );

    const conditionalViewValues = useConditionalViewValues({
      modelStatus: model.status,
      imageList
    });

    const {
      selectedModelVersionId,
      onSelectedModelVersionIdChange
    } = useModelVersionHandler(model.adoptedVersionId);

    const {
      startTrainingButtonLabel,
      startTrainingButtonIsDisabled,
      startTrainingButtonStatus,
      onStartTrainingClick
    } = useStartTrainingButtonHandler(model);

    /* extract values */

    const modelsHistoryViewValue = useStreamValue(modelsHistoryViewValue$);

    useLoadingPage(modelsHistoryViewValue?.status === 'pending');

    return (
      <>
        {modelsHistoryViewValue === undefined ? (
          undefined
        ) : modelsHistoryViewValue.status === promiseState.error ? (
          <NotFoundTemplate message={viewTexts.notFoundMessages.trainingList} />
        ) : (
          <ModelDetailView
            labelList={labelList}
            modelsHistory={modelsHistoryViewValue.value.latest}
            selectedModelVersionId={selectedModelVersionId}
            onSelectedModelVersionIdChange={onSelectedModelVersionIdChange}
            conditionalViewValues={conditionalViewValues}
            onEditLabelClick={onEditLabelClick}
            onEditModelImagesClick={onEditModelImagesClick}
            startTrainingButtonLabel={startTrainingButtonLabel}
            startTrainingButtonIsDisabled={startTrainingButtonIsDisabled}
            startTrainingButtonStatus={startTrainingButtonStatus}
            onStartTrainingClick={onStartTrainingClick}
          />
        )}
      </>
    );
  }
);

ModelDetail.displayName = 'ModelDetail';
